import React, { useEffect, useRef, useState } from 'react'
import { formatCurrency } from 'utils/currencyFormatter'
import { trackEvent } from 'utils/analytics'
import { couponSteadyVisible, welcomeCouponVisible } from 'utils/welcomeCouponVisibility'

// Welcome-coupon pill, shown to any viewer the server marks `claimable`
// (logged-out visitors and logged-in eligible students alike). Rendered
// `position: fixed` at `z-[10000]` (above every modal/lightbox/flash layer a
// visitor can reach). It sits at the very top center at every width: mobile
// `top-2`, width-constrained to clear the modal corner close/back/share
// controls (it MAY overlap the centered title label, never a control); desktop
// `lg:top-1`, above every modal, which are centred and start at 5vh. A fixed
// offset below the header band cannot work for modals of different heights.
// `data-welcome-coupon-pill` lets #flash-messages move below it (see
// application.tailwind.css). It is NOT dismissible. It is suppressed ONLY
// while its own login modal is open (see `suppressed`, logged-out flow only).
//
// CTA branches on auth:
// - logged-out → records welcome-coupon intent, then opens the login/registration
//   modal via onClaim (the unauthenticated registration path).
// - logged-in → calls the claim endpoint directly (never the intent endpoint,
//   never the login modal); on success the pill flips to a claimed confirmation
//   that names the expiry date.
//
// `held` is the unused coupon the viewer already holds ({ amount, currency,
// expiresOn }, server-formatted date). While it is set the pill stays on the
// page as a status saying how much is left and until when.
export default function WelcomeCouponBanner({ team, currentUser, teamSlug, translations, onClaim, suppressed = false, claimable = false, held = null, colors }) {
  const amount = Number(team?.welcome_coupon_amount)
  const currency = team?.welcome_coupon_currency || 'TWD'
  // null: not claimed in this view. Object: the coupon just claimed. false:
  // claimed, but the reply held no coupon to show.
  const [claimed, setClaimed] = useState(null)
  const submitting = useRef(false)

  // Steady-state visibility excludes `suppressed` so the impression effect does
  // not re-fire each time the login modal opens/closes.
  const steadyVisible = couponSteadyVisible({ claimable })
  const visible = welcomeCouponVisible({ claimable, suppressed })

  // Fire the impression once per page view, regardless of suppression toggles.
  const impressionFired = useRef(false)
  useEffect(() => {
    if (steadyVisible && !impressionFired.current) {
      impressionFired.current = true
      trackEvent('welcome_coupon_banner_impression', { team_slug: teamSlug })
    }
  }, [steadyVisible, teamSlug])

  const fillAmountAndDate = (template, coupon) =>
    (template || '')
      .replace('%{amount}', formatCurrency(Number(coupon.amount), coupon.currency || currency))
      .replace('%{date}', coupon.expiresOn || '')
  const ctaTemplate = currentUser
    ? (translations?.welcomeCoupon?.claimCta || translations?.welcomeCoupon?.cta || '')
    : (translations?.welcomeCoupon?.cta || '')
  const label = ctaTemplate.replace('%{amount}', formatCurrency(amount, currency))

  // Just claimed: a confirmation with the date. Already held: a reminder of
  // the amount left and the date. Both are statuses, not buttons.
  const statusCoupon = claimed || held
  const statusLabel = statusCoupon?.expiresOn
    ? fillAmountAndDate(claimed ? translations?.welcomeCoupon?.claimed : translations?.welcomeCoupon?.held, statusCoupon)
    : ''
  if (statusLabel.trim()) {
    return (
      <div
        role="status"
        aria-label={statusLabel}
        data-welcome-coupon-pill
        className="fixed top-2 lg:top-1 left-1/2 -translate-x-1/2 z-[10000] px-4 max-w-[calc(100vw-7rem)] lg:max-w-full"
      >
        <div
          className="flex items-center gap-2 max-w-full rounded-full px-4 py-1.5 lg:py-1 shadow-md text-white text-xs lg:text-sm font-medium"
          style={{ backgroundColor: colors?.primary || '#3366FF' }}
        >
          <span aria-hidden="true">{claimed ? '🎉' : '🎁'}</span>
          <span className="truncate min-w-0">{statusLabel}</span>
        </div>
      </div>
    )
  }

  // Hide entirely if not visible, if a claim went through with no coupon left
  // to show (claimed === false), or if the cta resolved to empty (missing
  // translation) — never ship a blank, unlabeled clickable pill.
  if (!visible || claimed === false || !label.trim()) return null

  const handleClaim = async () => {
    trackEvent('welcome_coupon_cta_click', { team_slug: teamSlug })

    if (currentUser) {
      // Logged-in: claim directly. Never touch the intent endpoint or login modal.
      // Guard against rapid double-clicks firing duplicate POSTs (server is safe via
      // the issuance lock, but this avoids wasting the per-user rate limit).
      if (submitting.current) return
      submitting.current = true
      try {
        const res = await fetch(`/api/teams/${team.id}/welcome_coupon_claim`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          // The page's own locale, so the date in the reply matches the page.
          body: JSON.stringify({ locale: document.documentElement.lang })
        })
        if (res.ok) {
          const body = await res.json().catch(() => ({}))
          // No dated coupon in the reply (used up or expired) leaves nothing to say.
          setClaimed(body?.held?.expiresOn ? body.held : false)
        }
      } catch (e) {
        // Non-blocking: leave the pill in place so the viewer can retry.
      } finally {
        submitting.current = false
      }
      return
    }

    // Logged-out: record intent, then enter the registration flow.
    try {
      await fetch(`/api/teams/${team.id}/welcome_coupon_intent`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      })
    } catch (e) {
      // Non-blocking: proceed to registration even if intent marking fails.
    }
    onClaim?.()
  }

  return (
    <div
      role="region"
      aria-label={label}
      data-welcome-coupon-pill
      className="fixed top-2 lg:top-1 left-1/2 -translate-x-1/2 z-[10000] px-4 max-w-[calc(100vw-7rem)] lg:max-w-full"
    >
      <button
        type="button"
        onClick={handleClaim}
        className="flex items-center gap-2 max-w-full rounded-full px-4 py-1.5 lg:py-1 shadow-md text-white text-xs lg:text-sm font-medium hover:opacity-90 transition-opacity"
        style={{ backgroundColor: colors?.primary || '#3366FF' }}
      >
        <span aria-hidden="true">🎁</span>
        <span className="truncate min-w-0">{label}</span>
      </button>
    </div>
  )
}
